import { getCredentialsFromCacheOrCognito } from "@amzn/midway-cognito-type-script-provider";
import { AwsClient } from "aws4fetch";

import { authStore } from "../../store/auth";
import { APPENV } from "../../utils/app.environment";
import { assumeInvocationRole } from "../credentials";

import type {
    CreateFeedbackRequest,
    CreateFeedbackResponse,
} from "./types/operations";
import type {
    CreateDeckDecorationRequest,
    CreateDeckDecorationResponse,
} from "./types/operations/createDeckDecoration";

export class G4SApiClient {
    public client: AwsClient;
    public endpoint: string;

    constructor(client: AwsClient, endpoint: string) {
        this.client = client;
        this.endpoint = endpoint;
    }

    createFeedback: (
        request: CreateFeedbackRequest,
    ) => Promise<CreateFeedbackResponse> = async ({
        dataContext,
        responseId,
        rating,
        freeFormFeedback,
        feedbackReasons,
        placement,
    }) => {
        const response = this.client.fetch(
            generateUrl(this.endpoint, "g4s/submit_feedback"),
            {
                headers: getHeaders(),
                method: "POST",
                body: JSON.stringify({
                    user_alias: authStore.getState().username,
                    response_id: responseId,
                    rating,
                    free_form_feedback: freeFormFeedback ?? "",
                    feedback_reasons: feedbackReasons ?? [],
                    placement_context: {
                        context: placement ?? "build_with_ai", // Keeping this here since G4S used this value for their telemetry
                    },
                    data_context: dataContext,
                }),
            },
        );
        return errorWrapper(response);
    };

    createDeckDecoration: (
        request: CreateDeckDecorationRequest,
    ) => Promise<CreateDeckDecorationResponse> = async ({
        salesforceAccountId,
        marketplaceId,
        documentUrl,
        outputOptions,
        customizationOptions,
        prompt,
        placement,
    }) => {
        const response = this.client.fetch(
            generateUrl(this.endpoint, "g4s/api/v1/deck/decorate"),
            {
                headers: getHeaders({
                    "x-amzn-user-alias": authStore.getState().username,
                    "x-amzn-placement": placement,
                }),
                method: "POST",
                body: JSON.stringify({
                    advertiser_salesforce_id: salesforceAccountId,
                    marketplace_id: marketplaceId,
                    document_url: documentUrl,
                    output_options: outputOptions,
                    customization_options: customizationOptions,
                    prompt,
                }),
            },
        );
        return errorWrapper(response);
    };
}

const getHeaders = (
    additionalHeaders: { [key: string]: string } = {},
): { [key: string]: string } => {
    return {
        "Content-Type": "application/json",
        user_alias: authStore.getState().username,
        ...additionalHeaders,
    };
};

const generateUrl = (endpoint: string, path: string): string => {
    return `${endpoint}/${path}`;
};

export const errorWrapper = async (api: Promise<Response>): Promise<any> => {
    try {
        const response = await api;
        if (response.ok) {
            return response.json();
        }
        const errorBody = await response.json();
        const message =
            errorBody?.message ??
            `Received status code ${response.status} without message`;
        return Promise.reject<Error>(new Error(message));
    } catch (e: any) {
        return Promise.reject<Error>(new Error(e.message));
    }
};

export async function getG4SClient(): Promise<G4SApiClient> {
    try {
        const {
            settings: {
                cognitoPoolId,
                region,
                g4sApiEndpoint,
                g4sApiInvocationRoleArn,
            },
        } = authStore.getState();

        const { accessKeyId, secretAccessKey, sessionToken } =
            await getCredentialsFromCacheOrCognito({
                region,
                identityPoolId: cognitoPoolId,
                clientId: window.location.host,
                redirectUri: window.location.href,
            });
        const credentials = await assumeInvocationRole(
            region,
            accessKeyId,
            secretAccessKey,
            sessionToken!,
            g4sApiInvocationRoleArn,
        );
        const awsClient = new AwsClient({
            accessKeyId: credentials.AccessKeyId!,
            secretAccessKey: credentials.SecretAccessKey!,
            sessionToken: credentials.SessionToken,
            region,
            retries: 0,
            service: "lambda",
        });
        const endpoint = `${APPENV.mock_server ? "http" : "https"}://${g4sApiEndpoint}`;
        return new G4SApiClient(awsClient, endpoint);
    } catch (e: any) {
        throw new Error(e.message);
    }
}
